import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core'
import { Subscription, interval } from 'rxjs'
import { ClipboardService } from 'src/app/services/clipboard.service'
import { CopyFeedbackService } from 'src/app/shared/copy-feedback.service'
import {
  ERROR_GLYPHS,
  ErrorAction,
  PresentedError,
  PresentedErrorItem,
  formatErrorReference,
  formatCountdown,
  hasReferenceDetail,
} from 'src/utils/presented-error'

/**
 * How the block is framed by its host. The anatomy is identical in all four; only the
 * container chrome changes.
 */
export type ErrorBlockVariant = 'block' | 'row' | 'banner' | 'toast'

/**
 * The single renderer for every error in the console. Owns all four severity themes, so
 * error colors are never redefined at a call site.
 *
 * Deliberately unlike a native issue row: white fill, a full severity border with no left
 * rule, a filled glyph chip, `$modal-radius`, and always at least one action.
 */
@Component({
  selector: 'arp-error-block',
  templateUrl: './error-block.component.html',
  styleUrls: ['./error-block.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CopyFeedbackService],
})
export class ErrorBlockComponent implements OnDestroy {
  readonly copyGlyph = ERROR_GLYPHS.copy
  readonly checkGlyph = ERROR_GLYPHS.check
  readonly dismissGlyph = ERROR_GLYPHS.dismiss

  @Input() variant: ErrorBlockVariant = 'block'
  @Input() dismissible = false

  /** Fired for actions the block doesn't own, so the host can run the remedy. */
  @Output() actionInvoked = new EventEmitter<ErrorAction>()
  @Output() dismissed = new EventEmitter<void>()

  /** Derived once per error rather than in template bindings, which allocate per CD pass. */
  referenceLine: string | null = null
  applicationRows: PresentedErrorItem[] = []
  issueRows: PresentedErrorItem[] = []

  get copied(): boolean {
    return !!this.copyFeedback.copiedStates['details']
  }

  /** Seconds left on a throttled action, keyed by action id. */
  countdowns: Record<string, number> = {}

  private _error: PresentedError | null = null
  private countdownTicker: Subscription = null
  /** `detectChanges` on a destroyed view throws, and the ticker runs outside the zone. */
  private destroyed = false

  /**
   * Whether the message region took a scrollbar. A scrollable region has to be focusable or a
   * keyboard user cannot reach what it hides — but an unconditional tab stop on every toast,
   * most of which fit, is its own nuisance. Measured once per error rather than bound.
   */
  bodyScrolls = false

  @ViewChild('body') private body?: ElementRef<HTMLElement>

  constructor(
    private readonly clipboard: ClipboardService,
    private readonly copyFeedback: CopyFeedbackService,
    private readonly cdr: ChangeDetectorRef,
    private readonly host: ElementRef<HTMLElement>,
    private readonly zone: NgZone
  ) {}

  /**
   * A failure the user just caused interrupts the screen reader; an informational block waits
   * for a pause. Computed here rather than as a getter so it is not re-derived every check.
   */
  ariaRole: 'alert' | 'status' = 'alert'

  @Input()
  set error(value: PresentedError | null) {
    this._error = value
    this.ariaRole = value?.severity === 'info' || value?.severity === 'neutral' ? 'status' : 'alert'
    if (value) this.revealSelf()
    this.referenceLine =
      value?.reference && hasReferenceDetail(value.reference) ? formatErrorReference(value.reference) : null

    this.startCountdowns(value)
    this.measureBody()

    const items = value?.items ?? []
    // Per-application rows render as a list; a single application's issues render as bullets.
    this.applicationRows = items.filter((item) => item.kind === 'application')
    this.issueRows = items.filter((item) => item.kind === 'issue')

    // Successful rows list last so a partial outcome reads failures first.
    if (this.applicationRows.length) {
      this.applicationRows = [
        ...this.applicationRows.filter((item) => !item.ok),
        ...this.applicationRows.filter((item) => item.ok),
      ]
    }
  }

  get error(): PresentedError | null {
    return this._error
  }

  ngOnDestroy(): void {
    this.destroyed = true
    this.countdownTicker?.unsubscribe()
  }

  /** Label for an action, with the remaining wait when it is throttled. */
  actionLabel(action: ErrorAction): string {
    const left = this.countdowns[action.id]
    return left ? `${action.label} ${formatCountdown(left)}` : action.label
  }

  /**
   * Ticks any throttled action down on the control itself, so the user watches the wait
   * expire instead of re-reading a number that was true when the error arrived.
   */
  private startCountdowns(value: PresentedError | null): void {
    this.countdownTicker?.unsubscribe()
    this.countdownTicker = null
    this.countdowns = {}

    const throttled = (value?.actions ?? []).filter((action) => action.countdownSeconds > 0)
    if (!throttled.length) return

    for (const action of throttled) this.countdowns[action.id] = action.countdownSeconds

    // Outside Angular, and rendered with a local `detectChanges`: a ticking control must not
    // drive an application-wide change detection pass once a second.
    this.zone.runOutsideAngular(() => {
      this.countdownTicker = interval(1000).subscribe(() => {
        let running = false
        for (const id of Object.keys(this.countdowns)) {
          if (this.countdowns[id] > 0) {
            this.countdowns[id] -= 1
            running = running || this.countdowns[id] > 0
          }
        }
        if (!running) {
          this.countdownTicker?.unsubscribe()
          this.countdownTicker = null
          // The wait is over, so the surface holding this error stops treating it as blocking.
          // Re-entering the zone is what lets that host's own footer re-render with it.
          this.zone.run(() => {
            if (this._error) this._error.blockedReason = undefined
            this.cdr.markForCheck()
          })
          return
        }
        if (!this.destroyed) this.cdr.detectChanges()
      })
    })
  }

  onAction(action: ErrorAction, event: MouseEvent): void {
    if (action.disabled || this.countdowns[action.id]) return

    if (action.id === 'copy-details') {
      event.preventDefault()
      this.copyDetails()
      return
    }

    // Links navigate on their own; everything else is the host's to handle.
    if (action.href) return
    event.preventDefault()
    this.actionInvoked.emit(action)
  }

  onRemedy(item: PresentedErrorItem, event: MouseEvent): void {
    if (!item.remedy) return
    event.preventDefault()
    this.actionInvoked.emit({
      id: 'item-remedy',
      label: item.remedy.label,
      route: item.remedy.route,
    })
  }

  onDismiss(): void {
    this.dismissed.emit()
  }

  /**
   * A dialog's footer is sticky, so SAVE stays clickable from the top of a long form — which
   * means the error answering that click can render below the fold with nothing to point at
   * it. `nearest` does nothing when the block is already visible, and the CSS
   * `scroll-margin` on the host keeps it clear of the pinned footer. Toasts are already
   * fixed to the viewport and must never move the page.
   */
  /** Reads layout once after the new content paints, never from a template binding. */
  private measureBody(): void {
    if (this.variant !== 'toast') return
    this.zone.runOutsideAngular(() =>
      requestAnimationFrame(() => {
        const element = this.body?.nativeElement
        const scrolls = !!element && element.scrollHeight > element.clientHeight
        if (scrolls === this.bodyScrolls || this.destroyed) return
        this.zone.run(() => {
          this.bodyScrolls = scrolls
          this.cdr.markForCheck()
        })
      })
    )
  }

  private revealSelf(): void {
    const element = this.host.nativeElement
    // jsdom has no scrollIntoView, so specs render the block without one.
    if (this.variant === 'toast' || typeof element.scrollIntoView !== 'function') return

    // Outside Angular: this is a paint concern and should not schedule another check.
    this.zone.runOutsideAngular(() => setTimeout(() => element.scrollIntoView({ block: 'nearest', behavior: 'auto' })))
  }

  actionTrackBy(_index: number, action: ErrorAction): string {
    return action.id
  }

  itemTrackBy(index: number, item: PresentedErrorItem): string {
    return `${item.id ?? ''}:${index}`
  }

  /** Copies code, cause code, request id, timestamp and the verbatim backend string. */
  private copyDetails(): void {
    const payload = [this._error?.title, this._error?.detail, this.referenceLine].filter(Boolean).join('\n')

    this.clipboard.copy(payload).then((ok) => {
      if (ok) this.copyFeedback.onCopied('details', this.cdr)
    })
  }
}
